import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Customer, User, Note, Task, Activity, SimCall } from '../../types';
import { encodeId, decodeId } from '../../utils/hashids';
import {
  fetchCustomer, updateCustomer, deleteCustomer,
  fetchUsersList, createNote,
  fetchNotes, fetchTasks, fetchActivities,
  fetchCustomerSimCalls,
  deleteNote, updateTask, createTask,
} from '../../services/api';

type Tab = 'overview' | 'qualification' | 'timeline';
type TLFilter = 'all' | 'notes' | 'tasks' | 'status' | 'calls';

const TEMP_COLOR: Record<string, string> = {
  HOT: '#D85A30', WARM: '#EF9F27', COLD: '#64748B',
};

const STATUS_COLOR: Record<string, { bg: string; color: string }> = {
  active:   { bg: '#dcfce7', color: '#166534' },
  inactive: { bg: '#f1f5f9', color: '#64748b' },
  at_risk:  { bg: '#fef9c3', color: '#854d0e' },
  churned:  { bg: '#fee2e2', color: '#991b1b' },
};

const QUAL_ITEMS = [
  { key: 'budget_confirmed',           label: 'Budget confirmed',           desc: 'Monthly order value stated' },
  { key: 'decision_maker_reached',     label: 'Decision maker reached',     desc: 'Spoke directly with proprietor/owner' },
  { key: 'timeline_stated',            label: 'Timeline stated',            desc: 'Customer has a start/renewal date in mind' },
  { key: 'product_interest_confirmed', label: 'Product interest confirmed', desc: 'Specific product range identified' },
];

const STATUSES = ['active', 'inactive', 'at_risk', 'churned'] as const;

const initials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const timeAgo = (iso?: string | null) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const fmtInr = (n?: number | null) =>
  n ? `₹${Number(n).toLocaleString('en-IN')}/mo` : '—';

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const fmtDuration = (s: number | null) => {
  if (!s) return '';
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <div className="ld-info-row">
    <span className="ld-info-label">{label}</span>
    <span className="ld-info-value">{value || <em className="ld-empty">—</em>}</span>
  </div>
);

const ACTIVITY_ICON: Record<string, string> = {
  created: '✦', updated: '✎', status_changed: '⇄', stage_changed: '⇄',
  assigned: '👤', note_added: '📝', task_added: '☑', task_completed: '✓', voice_call: '📞',
};

type FeedItem =
  | { kind: 'activity'; ts: string; data: Activity }
  | { kind: 'simcall';  ts: string; data: SimCall }
  | { kind: 'note';     ts: string; data: Note }
  | { kind: 'task';     ts: string; data: Task };

const TL_FILTERS: { key: TLFilter; label: string }[] = [
  { key: 'all',    label: 'All' },
  { key: 'status', label: 'Status Changes' },
  { key: 'calls',  label: 'Calls' },
  { key: 'notes',  label: 'Notes' },
  { key: 'tasks',  label: 'Tasks' },
];

/* ── Unified Timeline ───────────────────────────────────────────────────── */
const CustomerTimeline: React.FC<{ customerId: number; refreshKey?: number }> = ({ customerId, refreshKey = 0 }) => {
  const [items, setItems]     = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState<TLFilter>('all');
  const [addingTask, setAddingTask]   = useState(false);
  const [taskTitle, setTaskTitle]     = useState('');
  const [taskDue, setTaskDue]         = useState('');
  const [taskPriority, setTaskPriority] = useState<'low'|'medium'|'high'>('medium');
  const [savingTask, setSavingTask]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetchActivities('customers', customerId) as Promise<Activity[]>,
      fetchNotes('customers', customerId) as Promise<Note[]>,
      fetchTasks('customers', customerId) as Promise<Task[]>,
      fetchCustomerSimCalls(customerId),
    ]).then(([activities, notes, tasks, simCalls]) => {
      const feed: FeedItem[] = [
        ...activities.map(a => ({ kind: 'activity' as const, ts: a.created_at, data: a })),
        ...notes.map(n => ({ kind: 'note' as const, ts: n.created_at, data: n })),
        ...tasks.map(t => ({ kind: 'task' as const, ts: t.created_at, data: t })),
        ...simCalls.map(s => ({ kind: 'simcall' as const, ts: s.started_at, data: s })),
      ];
      feed.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
      setItems(feed);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleDeleteNote = async (noteId: number) => {
    await deleteNote('customers', customerId, noteId);
    setItems(prev => prev.filter(i => !(i.kind === 'note' && i.data.id === noteId)));
  };

  const handleToggleTask = async (taskId: number, isDone: boolean) => {
    const updated = await updateTask('customers', customerId, taskId, { is_done: !isDone });
    setItems(prev => prev.map(i => i.kind === 'task' && i.data.id === taskId ? { ...i, data: updated } : i));
  };

  const handleAddTask = async () => {
    if (!taskTitle.trim()) return;
    setSavingTask(true);
    try {
      const created: Task = await createTask('customers', customerId, {
        title: taskTitle.trim(), due_date: taskDue || undefined, priority: taskPriority,
      });
      setItems(prev => [{ kind: 'task', ts: created.created_at, data: created }, ...prev]);
      setTaskTitle(''); setTaskDue(''); setTaskPriority('medium'); setAddingTask(false);
    } catch {} finally { setSavingTask(false); }
  };

  const filtered = items.filter(i => {
    if (filter === 'all')    return true;
    if (filter === 'calls')  return i.kind === 'simcall';
    if (filter === 'notes')  return i.kind === 'note';
    if (filter === 'tasks')  return i.kind === 'task';
    if (filter === 'status') return i.kind === 'activity' && ['status_changed', 'assigned', 'created'].includes(i.data.type);
    return true;
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {TL_FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            border: '1.5px solid',
            borderColor: filter === f.key ? '#7c3aed' : 'var(--border)',
            background: filter === f.key ? '#ede9fe' : '#fff',
            color: filter === f.key ? '#7c3aed' : 'var(--text-muted)', cursor: 'pointer',
          }}>{f.label}</button>
        ))}
        {(filter === 'tasks' || filter === 'all') && !addingTask && (
          <button onClick={() => setAddingTask(true)} style={{
            marginLeft: 'auto', padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            border: '1.5px solid #16a34a', background: '#dcfce7', color: '#166534', cursor: 'pointer',
          }}>+ Add Task</button>
        )}
      </div>

      {addingTask && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#166534', marginBottom: 10 }}>New Task</div>
          <input className="form-input" placeholder="Task title *" value={taskTitle} onChange={e => setTaskTitle(e.target.value)}
            style={{ width: '100%', marginBottom: 8 }} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddTask()} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input type="date" className="form-input" value={taskDue} onChange={e => setTaskDue(e.target.value)} style={{ flex: 1 }} />
            <select className="form-input" value={taskPriority} onChange={e => setTaskPriority(e.target.value as any)} style={{ flex: 1 }}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn-secondary" onClick={() => { setAddingTask(false); setTaskTitle(''); }} style={{ fontSize: 13 }}>Cancel</button>
            <button className="btn-primary" onClick={handleAddTask} disabled={savingTask || !taskTitle.trim()} style={{ fontSize: 13, minWidth: 100 }}>
              {savingTask ? 'Saving…' : 'Add Task'}
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="spinner">Loading…</div> : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 14 }}>No items yet.</div>
      ) : (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', left: 15, top: 8, bottom: 8, width: 2, background: 'var(--border)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {filtered.map((item, idx) => (
              <div key={`${item.kind}-${item.data.id}-${idx}`} style={{ display: 'flex', gap: 16, paddingBottom: 20 }}>
                <div style={{
                  flexShrink: 0, width: 32, height: 32, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, zIndex: 1,
                  background: item.kind === 'note' ? '#fef9c3' : item.kind === 'task' ? '#dcfce7' : item.kind === 'simcall' ? '#e0f2fe' : '#f1f5f9',
                  border: '2px solid',
                  borderColor: item.kind === 'note' ? '#fde047' : item.kind === 'task' ? '#86efac' : item.kind === 'simcall' ? '#38bdf8' : 'var(--border)',
                }}>
                  {item.kind === 'note' ? '📝' : item.kind === 'task' ? (item.data.is_done ? '✓' : '☐') : item.kind === 'simcall' ? '📱' : ACTIVITY_ICON[item.data.type] ?? '·'}
                </div>
                <div style={{ flex: 1, paddingTop: 4 }}>
                  {item.kind === 'activity' && (
                    <div>
                      <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5 }}>{item.data.description}</div>
                      {item.data.user && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>by {item.data.user.name}</div>}
                    </div>
                  )}
                  {item.kind === 'simcall' && (
                    <div style={{ background: '#f0f9ff', border: '1px solid #38bdf8', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#0369a1' }}>
                        📱 SIM Call — {item.data.called_phone}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {item.data.duration_seconds > 0 && <span>Duration: {fmtDuration(item.data.duration_seconds)}</span>}
                        {item.data.device?.device_name && <span>Device: {item.data.device.device_name}</span>}
                        <span style={{
                          padding: '1px 7px', borderRadius: 20, fontWeight: 600, fontSize: 11,
                          background: item.data.status === 'processed' ? '#dcfce7' : '#f1f5f9',
                          color: item.data.status === 'processed' ? '#166534' : '#64748b',
                        }}>{item.data.status}</span>
                      </div>
                    </div>
                  )}
                  {item.kind === 'note' && (
                    <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ fontSize: 14, color: 'var(--text)', whiteSpace: 'pre-wrap', flex: 1 }}>{item.data.content}</div>
                        <button onClick={() => handleDeleteNote(item.data.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, marginLeft: 8, flexShrink: 0 }}>✕</button>
                      </div>
                      {item.data.user && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>by {item.data.user.name}</div>}
                    </div>
                  )}
                  {item.kind === 'task' && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: item.data.is_done ? '#f0fdf4' : '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                      <button onClick={() => handleToggleTask(item.data.id, item.data.is_done)} style={{
                        flexShrink: 0, width: 20, height: 20, borderRadius: 4, border: '2px solid', cursor: 'pointer',
                        borderColor: item.data.is_done ? '#16a34a' : '#94a3b8',
                        background: item.data.is_done ? '#16a34a' : '#fff', color: '#fff', fontSize: 12,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>{item.data.is_done ? '✓' : ''}</button>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, color: 'var(--navy)', fontWeight: 500, textDecoration: item.data.is_done ? 'line-through' : 'none', opacity: item.data.is_done ? 0.6 : 1 }}>
                          {item.data.title}
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 3, flexWrap: 'wrap' }}>
                          {item.data.due_date && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Due: {item.data.due_date}</span>}
                          {item.data.priority && (
                            <span style={{
                              fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 20,
                              background: item.data.priority === 'high' ? '#fee2e2' : item.data.priority === 'medium' ? '#fef9c3' : '#f1f5f9',
                              color:      item.data.priority === 'high' ? '#dc2626' : item.data.priority === 'medium' ? '#854d0e' : '#64748b',
                            }}>{item.data.priority}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{fmtDate(item.ts)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── CustomerDetail ─────────────────────────────────────────────────────── */
const CustomerDetail: React.FC = () => {
  const { id: idHash } = useParams<{ id: string }>();
  const numId          = decodeId(idHash) ?? 0;
  const history        = useHistory();

  const [customer, setCustomer]   = useState<Customer | null>(null);
  const [agents, setAgents]       = useState<User[]>([]);
  const [tab, setTab]             = useState<Tab>('overview');
  const [error, setError]         = useState('');
  const [quickNote, setQuickNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteCount, setNoteCount] = useState(0);
  const quickNoteRef = useRef<HTMLTextAreaElement>(null);

  const reload = () =>
    fetchCustomer(numId).then(setCustomer).catch(() => setError('Customer not found'));

  useEffect(() => {
    reload();
    fetchUsersList().then(setAgents).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idHash]);

  if (error)    return <div className="ld-page"><p className="ld-empty">Customer not found.</p></div>;
  if (!customer) return <div className="ld-page"><div className="spinner">Loading…</div></div>;

  const score     = QUAL_ITEMS.filter(q => !!(customer as any)[q.key]).length;
  const temp      = customer.temperature ?? 'COLD';
  const tempColor = TEMP_COLOR[temp] ?? '#64748B';
  const statusStyle = STATUS_COLOR[customer.status] ?? { bg: '#f1f5f9', color: '#64748b' };

  const patch = async (data: object) => {
    const updated = await updateCustomer(customer.id, data);
    setCustomer(updated);
  };

  const handleQualToggle = (field: string, current: boolean) => patch({ [field]: !current });

  const handleQuickNote = async () => {
    if (!quickNote.trim()) return;
    setSavingNote(true);
    try {
      await createNote('customers', customer.id, quickNote.trim());
      setQuickNote('');
      setNoteCount(n => n + 1);
      if (tab !== 'timeline') setTab('timeline');
    } finally { setSavingNote(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete customer "${customer.name}"?`)) return;
    await deleteCustomer(customer.id);
    history.push('/customers');
  };

  return (
    <div className="ld-page">
      <button className="breadcrumb-back" onClick={() => history.push('/customers')}>
        ← Back to Customers
      </button>

      <div className="ld-layout">
        {/* ── MAIN COLUMN ─────────────────────────────── */}
        <div className="ld-main">
          {/* Header card */}
          <div className="ld-header-card">
            <div className="ld-header-top">
              <div className="ld-avatar">{initials(customer.name)}</div>
              <div className="ld-hero">
                <div className="ld-hero-row">
                  <h1 className="ld-name">{customer.name}</h1>
                  <span className="temp-pill" style={{ background: tempColor }}>{temp}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: statusStyle.bg, color: statusStyle.color }}>
                    {customer.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="ld-company">
                  {[customer.company, customer.city, customer.state].filter(Boolean).join(' · ')}
                </div>
                <div className="ld-contact-row">
                  {(customer.mobile || customer.phone) && (
                    <a className="ld-phone" href={`tel:${customer.mobile ?? customer.phone}`}>
                      {customer.mobile ?? customer.phone}
                    </a>
                  )}
                  {customer.email && <span className="ld-email">{customer.email}</span>}
                </div>
              </div>
              <div className="ld-header-meta">
                <span className="source-pill">{(customer.source ?? 'manual').toUpperCase()}</span>
                <span className="ld-time">{timeAgo(customer.last_touch_at ?? customer.updated_at)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="ld-actions">
              <button className="ld-btn ld-btn-dial">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.01L6.6 10.8z"/></svg>
                Dial
              </button>
              <button className="ld-btn ld-btn-note" onClick={() => { setTab('timeline'); setTimeout(() => quickNoteRef.current?.focus(), 50); }}>
                + Note
              </button>
              <button className="ld-btn ld-btn-more" onClick={() => history.push(`/customers/${encodeId(customer.id)}/edit`)}>Edit</button>
              <button className="ld-btn ld-btn-danger" onClick={handleDelete}>Delete</button>
            </div>

            {/* Tabs */}
            <div className="ld-tabs">
              {(['overview', 'qualification', 'timeline'] as Tab[]).map(t => (
                <button key={t} className={`ld-tab${tab === t ? ' ld-tab-active' : ''}`} onClick={() => setTab(t)}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Tab bodies */}
          <div className="ld-tab-body">
            {tab === 'overview' && (
              <div className="ld-overview">
                <div className="ld-section">
                  <h3 className="ld-section-title">Contact Info</h3>
                  <div className="ld-info-grid">
                    <InfoRow label="Mobile"       value={customer.mobile ?? customer.phone} />
                    <InfoRow label="WhatsApp"      value={customer.whatsapp_number} />
                    <InfoRow label="Email"         value={customer.email} />
                    <InfoRow label="City"          value={customer.city} />
                    <InfoRow label="District"      value={customer.district} />
                    <InfoRow label="State"         value={customer.state} />
                    <InfoRow label="Drug Licence"  value={customer.drug_licence_number} />
                    <InfoRow label="GST No."       value={customer.gst_number} />
                  </div>
                </div>
                <div className="ld-section">
                  <h3 className="ld-section-title">Business Profile</h3>
                  <div className="ld-info-grid">
                    <InfoRow label="Business Type"       value={customer.business_type} />
                    <InfoRow label="Speciality"          value={(customer.specialty_interest ?? []).join(', ') || undefined} />
                    <InfoRow label="MOQ Estimate"        value={customer.moq_min != null && customer.moq_max != null
                      ? `₹${Number(customer.moq_min).toLocaleString('en-IN')} – ${Number(customer.moq_max).toLocaleString('en-IN')}` : undefined} />
                    <InfoRow label="Coverage Districts"  value={customer.coverage_districts} />
                    <InfoRow label="Current Supplier"    value={customer.current_supplier} />
                    <InfoRow label="MR Count"            value={customer.mr_count?.toString()} />
                    <InfoRow label="Years in Business"   value={customer.years_in_business?.toString()} />
                    <InfoRow label="ERP Software"        value={customer.erp_software} />
                  </div>
                </div>
                <div className="ld-section">
                  <h3 className="ld-section-title">Status</h3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {STATUSES.map(s => (
                      <button key={s}
                        onClick={() => patch({ status: s })}
                        style={{
                          padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                          border: '2px solid',
                          borderColor: customer.status === s ? (STATUS_COLOR[s]?.color ?? '#64748b') : 'var(--border)',
                          background: customer.status === s ? (STATUS_COLOR[s]?.bg ?? '#f1f5f9') : '#fff',
                          color: customer.status === s ? (STATUS_COLOR[s]?.color ?? '#64748b') : 'var(--text-muted)',
                          cursor: 'pointer',
                        }}>
                        {s.replace('_', ' ').toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tab === 'qualification' && (
              <div className="ld-qualification">
                <div className="qual-header">
                  <div>
                    <div className="qual-title">Qualification Score</div>
                    <div className="qual-sub">Track BANT criteria for this customer</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="qual-score-big">{score}/4</span>
                    <span className="temp-pill" style={{ background: tempColor }}>{temp}</span>
                  </div>
                </div>
                <div className="qual-bar">
                  <div className="qual-bar-fill" style={{ width: `${score * 25}%`, background: tempColor }} />
                </div>
                <div className="qual-list">
                  {QUAL_ITEMS.map(item => {
                    const checked = !!(customer as any)[item.key];
                    return (
                      <div key={item.key} className={`qual-item${checked ? ' qual-item-on' : ''}`}
                        onClick={() => handleQualToggle(item.key, checked)}>
                        <div className={`qual-check${checked ? ' qual-check-on' : ''}`}>{checked && '✓'}</div>
                        <div>
                          <div className="qual-item-label" style={{ color: checked ? 'var(--teal)' : 'var(--navy)' }}>{item.label}</div>
                          <div className="qual-item-desc">{item.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {tab === 'timeline' && (
              <div className="ld-section">
                <CustomerTimeline customerId={customer.id} refreshKey={noteCount} />
              </div>
            )}
          </div>
        </div>

        {/* ── SIDEBAR ─────────────────────────────────── */}
        <div className="ld-sidebar">
          {/* Qualification Score */}
          <div className="ld-card">
            <div className="ld-card-title">Qualification Score</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span className="qual-score-big" style={{ color: tempColor }}>{score}/4</span>
              <span className="temp-pill" style={{ background: tempColor }}>{temp}</span>
            </div>
            <div className="ld-qual-mini">
              {QUAL_ITEMS.map(item => {
                const ok = !!(customer as any)[item.key];
                return (
                  <div key={item.key} className="ld-qual-mini-row">
                    <span className={`ld-qual-dot${ok ? ' ld-qual-dot-ok' : ''}`}>{ok ? '✓' : ''}</span>
                    <span style={{ fontSize: 12, color: ok ? 'var(--teal)' : 'var(--text-muted)' }}>{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Note */}
          <div className="ld-card">
            <div className="ld-card-title">Quick Note</div>
            <textarea ref={quickNoteRef} className="ld-quick-note" placeholder="Type a note and click Save…" rows={3}
              value={quickNote} onChange={e => setQuickNote(e.target.value)} />
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }}
              onClick={handleQuickNote} disabled={savingNote || !quickNote.trim()}>
              {savingNote ? 'Saving…' : 'Save'}
            </button>
          </div>

          {/* Assignment */}
          <div className="ld-card">
            <div className="ld-card-title">Assignment</div>
            <div className="ld-assign-field">
              <label className="ld-assign-label">Assigned To</label>
              <select className="lead-form-input"
                value={customer.assigned_to?.toString() ?? ''}
                onChange={e => patch({ assigned_to: e.target.value || null })}>
                <option value="">Unassigned</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="ld-assign-field">
              <label className="ld-assign-label">Expected Close</label>
              <input className="lead-form-input" type="date"
                value={customer.expected_close_date ?? ''}
                onChange={e => patch({ expected_close_date: e.target.value || null })} />
            </div>
            <div className="ld-assign-field">
              <label className="ld-assign-label">Est. Order Value</label>
              <div className="ld-assign-val">{fmtInr(customer.expected_order_value)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;
